import { EditMode } from "@/types/editMode";
import {
  CreateModelCommandRequest,
  FilteredElementConfiguration,
  FilteredElementConfigurationType,
  ModelCommandResponse,
  ProblemDetails,
  ProfileLevelType,
  SupplementalValueType,
} from "@api/apiClient";
import { useCreateModelCommand, useUpdateModelCommand } from "@api/modelCommand";
import { useProfileSearch } from "@api/profiles";
import { useFilteredElementConfigurations } from "@api/queryBuilders";
import MaterialSelectDialog from "@components/MaterialSelectDialog/MaterialSelectDialog";
import NumberInput from "@components/NumberInput/NumberInput";
import RuleSetFilterForm from "@components/RuleSetFilterForm/RuleSetFilterForm";
import i18n from "@i18n/i18n";
import {
  Autocomplete,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Snackbar,
  TextField,
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import { GridCloseIcon } from "@mui/x-data-grid-premium";
import { isNumeric } from "@utils/isNumeric";
import _ from "lodash";
import { ChangeEvent, FormEvent, useEffect, useState } from "react";

export interface NonModelledElementDialogProps {
  open: boolean;
  filteredElementConfigurationId: string;
  onClose: () => void;
  editMode: EditMode;
  data?: ModelCommandResponse;
}

export interface DropdownModel {
  label: string;
  value: string;
}

const NonModelledElementDialog = (props: NonModelledElementDialogProps) => {
  const { onClose, open, editMode } = props;
  const [command] = useState("AddNonModelledElement"); // TODO: create type for command
  const [openMaterialDlg, setOpenMaterialDlg] = useState(false);
  const [openRuleSetDlg, setOpenRuleSetDlg] = useState(false);
  const [formData, setFormData] = useState<any>({
    name: "",
    description: "",
    value: 0,
    lowerLimit: 0,
    higherLimit: 100,
    co2MaterialType: { co2MaterialTypeId: "", name: "" },
    filteredElementConfiguration: null,
  });
  const [openEditCreateSuccess, setOpenEditCreateSuccess] = useState(false);
  ///Queries
  const createCommand = useCreateModelCommand();
  const updateCommand = useUpdateModelCommand(props.data?.commandId as string);

  const ruleSets = useFilteredElementConfigurations(FilteredElementConfigurationType.AddNonModelledElement);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [selectedProfileValue, setSelectedProfileValue] = useState<string>("");
  const [profileOptions, setProfileOptions] = useState<DropdownModel[]>([]);

  const profile = useProfileSearch([ProfileLevelType.Application], null);
  const [errors, setErrors] = useState({ value: "", isValueOutOfRange: false, lowerLimitError: "", isInvalidLowLimit: false });
  const [isDuplicateModalCommand, setIsDuplicateModalCommand] = useState(false);
  useEffect(() => {
    if (profile.data && profile.data.length > 0) {
      const profileOptions = profile.data.map((profile) => ({
        label: profile.name,
        value: profile.profileId,
      }));

      setProfileOptions(profileOptions as DropdownModel[]);

      // Set the first profile as the default value if it exists
      const firstProfile = profileOptions[0];
      if (firstProfile?.value) {
        setSelectedProfileValue(firstProfile.value);
        setFormData({
          ...formData,
          profileId: firstProfile.value,
          profileName: firstProfile.label,
        });
      }
    }
  }, [profile.data]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitted(true);
    if (formData.name?.trim().length !== 0 && validate()) {
      try {
        setIsDuplicateModalCommand(false);
        const commandRequest: any = createPayload(formData);

        editMode === EditMode.Add ? await createCommand.mutateAsync(commandRequest) : await updateCommand.mutateAsync(commandRequest);
        setOpenEditCreateSuccess(true);
        props.onClose();
      } catch (e) {
        console.log("Test---", e);
        const d = e as ProblemDetails;
        if (d && d.status === 409) {
          setIsDuplicateModalCommand(true);
        }
      } finally {
        setIsSubmitted(false);
      }
    }
  };

  const handleVolumeLimit = (percentVolume?: number, lowLimit?: string | number, highLimit?: string | number) => {
    const isValid = false;
    if (_.isFinite(percentVolume) && isNumeric(lowLimit) && isNumeric(highLimit)) {
      return Number(percentVolume) <= parseFloat(highLimit as string) && Number(percentVolume) >= parseFloat(lowLimit as string);
    } else if (_.isFinite(percentVolume) && lowLimit && _.isNil(highLimit)) {
      return Number(percentVolume) >= parseFloat(lowLimit as string);
    } else if (_.isFinite(percentVolume) && (_.isNil(lowLimit) || lowLimit === "0") && highLimit) {
      return Number(percentVolume) <= parseFloat(highLimit as string);
    } else if (percentVolume === 0 && (_.isNil(lowLimit) || lowLimit === "0") && highLimit === "0") {
      return true;
    }
    //In case when percent value is 0 ,low value is zero and high value is greater than zero
    else if (percentVolume === 0 && (lowLimit == null || lowLimit === undefined || lowLimit === "0") && highLimit && parseFloat(highLimit as string) > 0) {
      return true;
    }

    return isValid;
  };

  const validate = () => {
    const newErrors = { value: "", isValueOutOfRange: false, lowerLimitError: "", isInvalidLowLimit: false };
    let valid = true;

    // Low Limit and High Limit validation
    if (formData.lowerLimit > formData.higherLimit) {
      newErrors.lowerLimitError = i18n.t("non-model-element.add-non-model-element-dialog.form-elements.helper.lowlimit-range");
      newErrors.isInvalidLowLimit = true;
      valid = false;
    }

    // value range validation
    if (!newErrors.isInvalidLowLimit && !handleVolumeLimit(formData.value as number, formData.lowerLimit, formData.higherLimit)) {
      newErrors.value = i18n.t("non-model-element.add-non-model-element-dialog.form-elements.helper.value-range");
      newErrors.isValueOutOfRange = true;
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleProfileChange = (event: SelectChangeEvent) => {
    const { value } = event.target;
    setSelectedProfileValue(value as string);

    const name = profileOptions.find((x) => x.value === value);

    setFormData({ ...formData, profileId: name?.value } as any);
  };

  function createPayload(formData: any): CreateModelCommandRequest | undefined {
    try {
      return new CreateModelCommandRequest({
        name: formData.name,
        description: formData.description,
        // TODO: each ModelCommand should have a typed argument
        arguments: JSON.stringify({
          filteredElementConfigurationId: formData.filteredElementConfiguration?.filteredElementConfigurationId,
          co2MaterialTypeId: formData.co2MaterialType?.co2MaterialTypeId,
          value: formData.value,
          valueType: SupplementalValueType.Percent,
          lowerLimit: formData.lowerLimit,
          higherLimit: formData.higherLimit,
        }),
        command,
        profileId: formData.profileId,
      });
    } catch {}
  }

  const handleRuleSetChange = (_event: any, value: any) => {
    const ruleSet = ruleSets?.data?.find((x) => x.filteredElementConfigurationId === value.filteredElementConfigurationId);
    setFormData({ ...formData, filteredElementConfiguration: value });
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [event.target.name]: event.target.value } as any);
  };

  const handleVolumeChange = (value: number) => {
    if (value >= 0 && value <= 100) {
      setFormData({ ...formData, value } as any);
      setErrors({ ...errors, isValueOutOfRange: false, value: "" } as any);
    }
  };

  const handlelowerLimitChange = (value: number) => {
    if (value >= 0 && value <= 100) {
      setFormData({ ...formData, lowerLimit: value } as any);
    }
    setErrors({ ...errors, isInvalidLowLimit: false, lowerLimitError: "" } as any);
  };
  const handlehigherLimitChange = (value: number) => {
    if (value >= 0 && value <= 100) {
      setFormData({ ...formData, higherLimit: value } as any);
    }
  };

  const handleSelectMaterial = async (selectedMaterialType?: any) => {
    if (selectedMaterialType !== undefined) {
      setFormData({ ...formData, co2MaterialType: { co2MaterialTypeId: selectedMaterialType.co2MaterialTypeId, name: selectedMaterialType.name } });
    }
    setOpenMaterialDlg(false);
  };

  function populateFormData(object: any) {
    const argument = JSON.parse(object.arguments as any);
    setFormData({
      name: object.name,
      description: object.description,
      value: argument.value,
      lowerLimit: argument.lowerLimit,
      higherLimit: argument.higherLimit,
      filteredElementConfiguration: getSelectedfilteredElementConfiguration(argument.filteredElementConfigurationId),
      profileName: object.profileName,
      co2MaterialType: object.co2MaterialType,
      profileId: object?.profileId,
      oldName: object.name,
    });
    if (object.profileId) {
      setSelectedProfileValue(object.profileId);
    }
  }

  function resetNonElementConfigForm() {
    const firstProfile = profileOptions[0];
    setFormData({
      name: "",
      description: "",
      value: 0,
      co2MaterialType: null,
      filteredElementConfiguration: null,
      profileId: firstProfile?.value || "",
    });
    setSelectedProfileValue(firstProfile?.value || "");
  }

  //Populate Form in edit mode after Rule Sets are loaded
  useEffect(() => {
    if (props.editMode === EditMode.Edit && ruleSets.data?.length && ruleSets.data?.length) {
      populateFormData(props.data as ModelCommandResponse);
    }
  }, [props.editMode, props.filteredElementConfigurationId, ruleSets.data]);

  const handleRuleSetFilterDlgClose = (filteredElementConfiguration: FilteredElementConfiguration) => {
    setOpenRuleSetDlg(false);
    setFormData({ ...formData, filteredElementConfiguration });
  };

  const getSelectedfilteredElementConfiguration = (filteredElementConfigurationId: any): any => {
    const filteredElementConfiguration = ruleSets.data?.find((x) => x.filteredElementConfigurationId == filteredElementConfigurationId);
    return filteredElementConfiguration;
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth={true}
        maxWidth={"md"}
        aria-labelledby="non-model-element-dialog-title"
        aria-describedby="non-model-element-dialog-description"
      >
        <form onSubmit={handleSubmit}>
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {editMode === EditMode.Add
              ? i18n.t("non-model-element.add-non-model-element-dialog.add-non-model-element")
              : i18n.t("non-model-element.add-non-model-element-dialog.edit-non-model-element")}
            <IconButton aria-label="close" onClick={onClose}>
              <GridCloseIcon></GridCloseIcon>
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Grid container mt={1} spacing={2}>
              <Grid size={12}>
                <TextField
                  label={i18n.t("non-model-element.add-non-model-element-dialog.form-elements.name")}
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleInputChange}
                  variant="outlined"
                  required
                  error={(isSubmitted && _.isEmpty(formData.name?.trim())) || isDuplicateModalCommand}
                  helperText={
                    isSubmitted && _.isEmpty(formData.name?.trim())
                      ? i18n.t("non-model-element.add-non-model-element-dialog.form-elements.helper.name")
                      : isDuplicateModalCommand
                        ? i18n.t("non-model-element.add-non-model-element-dialog.form-elements.helper.duplicate-command")
                        : ""
                  }
                  fullWidth
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  label={i18n.t("non-model-element.add-non-model-element-dialog.form-elements.description")}
                  name="description"
                  type="text"
                  value={formData.description}
                  onChange={handleInputChange}
                  variant="outlined"
                  required
                  fullWidth
                />
              </Grid>
              <Grid size={12}>
                <Grid container spacing={2} sx={{ alignItems: "center" }}>
                  <Grid size="auto" sx={{ flexGrow: 1 }}>
                    <Autocomplete
                      id="rule-set-filter-select"
                      options={ruleSets?.data || []}
                      getOptionLabel={(option) => option?.name || ""}
                      getOptionKey={(option) => option?.filteredElementConfigurationId || ""}
                      autoHighlight
                      defaultValue={formData.filteredElementConfiguration}
                      value={formData.filteredElementConfiguration}
                      sx={{ width: "100%" }}
                      onChange={handleRuleSetChange}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={i18n.t("non-model-element.add-non-model-element-dialog.form-elements.rule-set")}
                          required={true}
                          slotProps={{
                            htmlInput: {
                              ...params.inputProps,
                              required: true,
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size="auto" sx={{ flexGrow: 0 }}>
                    <Button variant="outlined" onClick={() => setOpenRuleSetDlg(true)}>
                      {i18n.t("non-model-element.add-non-model-element-dialog.form-elements.edit-filter")}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
              <Grid container>
                <Grid size={4}>
                  <NumberInput
                    label={i18n.t("non-model-element.add-non-model-element-dialog.form-elements.percent-value")}
                    value={formData.value as number}
                    min={0}
                    max={100}
                    step={0.1}
                    onChange={handleVolumeChange}
                    unit={"%"}
                    required
                    sx={{
                      maxWidth: "150px",
                    }}
                    error={errors?.isValueOutOfRange}
                    helperText={errors.value}
                  />
                </Grid>

                <Grid size={4}>
                  <NumberInput
                    label={i18n.t("non-model-element.add-non-model-element-dialog.form-elements.lowerlimit")}
                    value={formData.lowerLimit as number}
                    min={0}
                    max={100}
                    step={0.1}
                    onChange={handlelowerLimitChange}
                    unit={"%"}
                    required
                    sx={{
                      maxWidth: "150px",
                    }}
                    error={errors?.isInvalidLowLimit}
                    helperText={errors.lowerLimitError}
                  />
                </Grid>

                <Grid size={4}>
                  <NumberInput
                    label={i18n.t("non-model-element.add-non-model-element-dialog.form-elements.higherlimit")}
                    value={formData.higherLimit as number}
                    min={0}
                    max={100}
                    step={0.1}
                    onChange={handlehigherLimitChange}
                    unit={"%"}
                    required
                    sx={{
                      maxWidth: "150px",
                    }}
                  />
                </Grid>
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  value={formData.co2MaterialType?.name || (i18n.t("non-model-element.add-non-model-element-dialog.form-elements.select-material") as string)}
                  sx={{ "& .MuiInputBase-input": { cursor: "pointer" } }}
                  onClick={() => setOpenMaterialDlg(true)}
                  label={i18n.t("non-model-element.add-non-model-element-dialog.form-elements.co2-material-type")}
                  placeholder={i18n.t("non-model-element.add-non-model-element-dialog.form-elements.select-material")}
                  slotProps={{
                    input: {
                      readOnly: true,
                    },
                  }}
                ></TextField>
              </Grid>
              <Grid size={12}>
                <FormControl fullWidth>
                  <InputLabel id="profile-label">{i18n.t("non-model-element.add-non-model-element-dialog.form-elements.select-profile")}</InputLabel>

                  <Select
                    labelId="profile-select"
                    id="profile-select"
                    value={selectedProfileValue}
                    onChange={handleProfileChange}
                    input={<OutlinedInput label={i18n.t("non-model-element.add-non-model-element-dialog.form-elements.select-profile")} />}
                  >
                    {profileOptions.map((profile) => (
                      <MenuItem key={profile.value} value={profile.value}>
                        {profile.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <></>
              </Grid>
              <Grid size={12}>
                <DialogActions>
                  <Button variant="outlined" onClick={onClose}>
                    {i18n.t("non-model-element.add-non-model-element-dialog.cancel")}
                  </Button>
                  <Button autoFocus variant="contained" type="submit">
                    {i18n.t("non-model-element.add-non-model-element-dialog.save")}
                  </Button>
                </DialogActions>
              </Grid>
            </Grid>
          </DialogContent>
        </form>
      </Dialog>
      <Dialog
        // fullWidth={true}
        // maxWidth={"sm"}
        fullWidth={true}
        maxWidth={"lg"}
        open={openRuleSetDlg}
        aria-labelledby="rulesetfilter-dialog-title"
        aria-describedby="rulesetfilter-dialog-description"
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {formData.filteredElementConfiguration?.filteredElementConfigurationId ? i18n.t("rule-set-filter-dialog.edit-header") : i18n.t("rule-set-filter-dialog.add-header")}
          <IconButton aria-label="close" onClick={() => setOpenRuleSetDlg(false)}>
            <GridCloseIcon></GridCloseIcon>
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <RuleSetFilterForm
            filteredElementConfigurationType={FilteredElementConfigurationType.AddNonModelledElement}
            filterConfigurationId={formData.filteredElementConfiguration?.filteredElementConfigurationId as string}
            OnClose={() => setOpenRuleSetDlg(false)}
            OnSubmit={(filterConfiguration) => handleRuleSetFilterDlgClose(filterConfiguration)}
          ></RuleSetFilterForm>
        </DialogContent>
      </Dialog>
      {openMaterialDlg && (
        <MaterialSelectDialog
          // ref={materialDlgRef}
          open={openMaterialDlg}
          onClose={() => handleSelectMaterial()}
          onSubmit={(selectedMaterialType) => handleSelectMaterial(selectedMaterialType)}
        />
      )}
      {openEditCreateSuccess && (
        <Snackbar
          open={openEditCreateSuccess}
          autoHideDuration={2000}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          message={
            editMode === EditMode.Add
              ? i18n.t("non-model-element.add-non-model-element-dialog.created-success")
              : i18n.t("non-model-element.add-non-model-element-dialog.edited-success")
          }
          onClose={() => setOpenEditCreateSuccess(false)}
        />
      )}
    </>
  );
};

export default NonModelledElementDialog;
